import type { OverallTaskStatus, TaskStatusPhase } from '@kaiizen/kai-pro-api';

import { OVERALL_TASK_STATUS_VALUES } from '@/Client/Lib/scopeTaskStatusLib';
import { TASK_STATUS_BADGE_LABEL, TaskStatusBadge } from '@/Client/Widgets/TaskStatusBadge';
import { ScopeFilterSection } from './ScopeFilterSection';
import { DEFAULT_SCOPE_FILTER_STYLES, type ScopeFilterStyles } from './scopeFilterStyles';

export type TaskStatusCounts = Record<OverallTaskStatus, number>;

export const TASK_STATUS_VALUES: readonly OverallTaskStatus[] = OVERALL_TASK_STATUS_VALUES;

const TASK_STATUS_DEFS: Record<TaskStatusPhase, readonly OverallTaskStatus[]> = {
  scoping: ['empty', 'draft', 'approved'],
  construction: ['notStarted', 'inProgress', 'needsRework', 'completed'],
};

const TASK_STATUS_STAGE_DEFS: readonly { phase: TaskStatusPhase; label: string }[] = [
  { phase: 'scoping', label: 'In Draft' },
  { phase: 'construction', label: 'In Construction' },
];

export const getTaskStatusSectionValues = (phase: TaskStatusPhase): readonly OverallTaskStatus[] => TASK_STATUS_DEFS[phase];

export const ScopeFilterTaskGroups = ({
  phase,
  counts,
  selectedStatus,
  onStatusToggle,
  styles = DEFAULT_SCOPE_FILTER_STYLES,
}: {
  phase: TaskStatusPhase;
  counts: TaskStatusCounts;
  selectedStatus?: OverallTaskStatus;
  onStatusToggle: (status: OverallTaskStatus) => void;
  styles?: ScopeFilterStyles;
}) => (
  <div data-testid={'scope-task-status-section'}>
    {TASK_STATUS_STAGE_DEFS.map(({ phase: stagePhase, label }) => {
      const isCurrentStage = stagePhase === phase;
      return (
        <div
          key={stagePhase}
          css={[styles.stage, !isCurrentStage && styles.disabledStage]}
          data-testid={`scope-task-status-stage-${stagePhase}`}
        >
          <div css={styles.stageHeading}>{label}</div>
          <ScopeFilterSection
            testId={`scope-task-status-rows-${stagePhase}`}
            styles={styles}
            rows={getTaskStatusSectionValues(stagePhase).map(status => ({
              key: status,
              label: TASK_STATUS_BADGE_LABEL[status],
              indicator: <TaskStatusBadge status={status} iconOnly={true} />,
              count: counts[status],
              selected: selectedStatus === status,
              disabled: !isCurrentStage,
              testId: `scope-task-status-${status}-${stagePhase}`,
              onToggle: () => onStatusToggle(status),
            }))}
          />
        </div>
      );
    })}
  </div>
);
